import { useSyncExternalStore } from "react"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { z } from "zod"
import { extractErrorCode, extractErrorDetailField, extractErrorStatus } from "@/lib/error-messages"
import { createOptionStore } from "@/lib/option-store"
import { serverCode } from "@/services/public-fetch"

// Organisme fermé : le serveur refuse ses membres (403 `TENANT_CLOSED`).
// Axios et AuthProvider y notent la date et le nom.
// ProtectedRoute en tire une page unique pour les non-administrateurs.

/** Ce que le refus du serveur dit de la fermeture. */
export type TenantClosure = {
  /** Nom de l'organisme (`details.tenant_name`). */
  tenantName: O.Option<string>
  /** Date de fermeture `AAAA-MM-JJ` (`details.closed_on`). */
  closedOn: O.Option<string>
}

// La fermeture notée.
const closureStore = createOptionStore<TenantClosure>()

/** Texte lu par l'apprenant ou le visiteur public (410). */
export const TENANT_CLOSED_PUBLIC_MESSAGE =
  "Ce service n'est plus disponible, car l'organisme de formation a fermé son espace. Pour toute question sur vos données, contactez-le directement."

function isClosedCode(code: unknown): boolean {
  return code === "TENANT_CLOSED"
}

/** `code` posé sur l'erreur elle-même (fetch des liens publics). */
const CodedError = z.object({ code: z.string() })

function ownErrorCode(error: unknown): O.Option<unknown> {
  if (!(error instanceof Error)) return O.none
  const parsed = CodedError.safeParse(error)
  return parsed.success ? O.some(parsed.data.code) : O.none
}

/**
 * Erreur d'un lien public ou du portail.
 * L'organisme a fermé son espace.
 *
 * @example isTenantClosedError({ response: { status: 410, data: { code: "TENANT_CLOSED" } } }) // true
 */
export function isTenantClosedError(error: unknown): boolean {
  const codes: readonly O.Option<unknown>[] = [
    O.fromNullable(extractErrorCode(error)),
    serverCode(error),
    ownErrorCode(error),
  ]
  return codes.some(O.exists(isClosedCode))
}

/**
 * Refus du serveur : l'organisme courant a fermé son espace.
 *
 * @example isTenantClosedRefusal({ response: { status: 403, data: { code: "TENANT_CLOSED" } } }) // true
 */
export function isTenantClosedRefusal(error: unknown): boolean {
  return extractErrorStatus(error) === 403 && extractErrorCode(error) === "TENANT_CLOSED"
}

function detailText(error: unknown, key: string): O.Option<string> {
  return pipe(
    extractErrorDetailField(error, key),
    O.filter((value): value is string => typeof value === "string" && value.trim() !== ""),
  )
}

/**
 * Date et nom portés par `details` du refus.
 *
 * @example tenantClosureOf(error) // { tenantName: O.some("Atelier"), closedOn: O.some("2026-10-09") }
 */
export function tenantClosureOf(error: unknown): TenantClosure {
  return { tenantName: detailText(error, "tenant_name"), closedOn: detailText(error, "closed_on") }
}

/**
 * Le premier refus fait foi. Les suivants portent la même fermeture.
 *
 * @example markTenantClosed(tenantClosureOf(error)) // useTenantClosed() rend O.some(...)
 */
export function markTenantClosed(next: TenantClosure): void {
  if (O.isSome(closureStore.get())) return
  closureStore.set(O.some(next))
}

/**
 * Changement d'organisme, connexion ou déconnexion : la fermeture notée tombe.
 *
 * @example resetTenantClosed() // useTenantClosed() rend O.none
 */
export function resetTenantClosed(): void {
  if (O.isNone(closureStore.get())) return
  closureStore.set(O.none)
}

/** Jour `AAAA-MM-JJ` écrit JJ/MM/AAAA. */
function frenchDay(isoDay: string): string {
  const [year, month, day] = isoDay.split("-")
  return `${day}/${month}/${year}`
}

// Jour `AAAA-MM-JJ` : quatre, deux, puis deux chiffres.
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/**
 * Texte d'un membre non administrateur.
 * Dit qui a fermé, quand, l'effacement prévu, qui contacter.
 * Jamais « administrateur » : l'organisme est responsable de ses données.
 *
 * @example tenantClosedMessage({ tenantName: O.none, closedOn: O.none }) // "Votre organisme a fermé son espace Qualneo. …"
 */
export function tenantClosedMessage(closure: TenantClosure): string {
  const who = pipe(
    closure.tenantName,
    O.match(
      () => "Votre organisme",
      (name) => `L'organisme ${name}`,
    ),
  )
  const [when, erasure] = pipe(
    closure.closedOn,
    O.filter((day) => ISO_DAY.test(day)),
    O.match(
      () => ["", "60 jours après la fermeture"],
      (day) => [` le ${frenchDay(day)}`, "60 jours après cette date"],
    ),
  )
  return `${who} a fermé son espace Qualneo${when}. Ses données seront effacées définitivement ${erasure}, ainsi que votre compte s'il n'est rattaché à aucun autre organisme. Pour récupérer des informations vous concernant ou exercer vos droits, adressez-vous à l'organisme, qui en est responsable.`
}

export function useTenantClosed(): O.Option<TenantClosure> {
  return useSyncExternalStore(closureStore.subscribe, closureStore.get, closureStore.get)
}
